import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    faImage,
    faVideo
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    FormGroup,
    FormControl,
    FormLabel,
    Image,
    Button
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import config from '../config/config';
import {
    useWebsite,
    setFlashMessage
} from '../contexts/WebsiteContext';
import { useMode } from '../contexts/ModeContext';
import { useResto } from '../contexts/RestoContext';
import MediaItem from '../components/MediaItem';
import MediaForm from '../components/MediaForm';
import { ucfirst } from '../utils/utils';
import { useAuth } from '../contexts/AuthContext';

function MediaList({
    mediaKey,
    maxItems = 10,
    isShortLine = false
}) {
    const { t } = useTranslation();
    const { isModeAdmin } = useMode();
    const { website, setIsLoading } = useWebsite();
    const { token } = useAuth();
    const { restoData } = useResto();

    // ✅ SAFE fallback
    const availableLanguages =
        restoData?.availableLanguages || [];

    const { apiUrl } = config;
    const apiPath = `${apiUrl}media`;

    const [mediaList, setMediaList] = useState([]);
    const [showModal, setShowModal] =
        useState(false);
    const [selectedAction, setSelectedAction] =
        useState(null);

    const initialFormData = {
        labelFr: '',
        labelEn: '',
        labelDe: '',
        labelEs: '',
        detailsFr: '',
        detailsEn: '',
        detailsDe: '',
        detailsEs: '',
        videoUrl: '',
        file: null,
        mediaKey: mediaKey,
        website: website,
    };

    const [formData, setFormData] =
        useState(initialFormData);

    // ✅ Fetch with safety
    const fetchItems = async () => {
        if (!mediaKey || !website) return;

        try {
            const response = await axios.get(
                `${apiPath}?mediaKey=${mediaKey}&website=${website}`
            );

            if (Array.isArray(response.data)) {
                setMediaList(
                    response.data.slice(0, maxItems)
                );
            }
        } catch (error) {
            setFlashMessage(
                t('ERROR_MESSAGE'),
                'error'
            );
        }
    };

    useEffect(() => {
        fetchItems();
    }, [mediaKey, website, maxItems]);

    const handleAddPhoto = () => {
        setSelectedAction('photo');
        setShowModal(true);
    };

    const handleAddVideo = () => {
        setSelectedAction('video');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAction(null);
        setFormData(initialFormData);
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0] || null;

        setFormData(prev => ({
            ...prev,
            file
        }));
    };

    const handleVideoUrlChange = (e) => {
        setFormData(prev => ({
            ...prev,
            videoUrl: e.target.value || ''
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value || ''
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!selectedAction) return;

        setIsLoading(true);

        try {
            const formDataWithMedia =
                new FormData();

            Object.entries(formData).forEach(
                ([key, value]) => {
                    if (value !== null) {
                        formDataWithMedia.append(
                            key,
                            value
                        );
                    }
                }
            );

            formDataWithMedia.append(
                'mediaType',
                selectedAction
            );

            await axios.post(
                `${config.apiUrl}media`,
                formDataWithMedia,
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`,
                        'Content-Type':
                            'multipart/form-data'
                    }
                }
            );

            await fetchItems();
            handleCloseModal();

            setFlashMessage(
                t('SUCCESS_CREATED_MESSAGE'),
                'success'
            );

        } catch (error) {
            setFlashMessage(
                t('ERROR_MESSAGE'),
                'error'
            );
        }

        setIsLoading(false);
    };

    const handleDelete = async (mediaId) => {
        if (!mediaId) return;

        setIsLoading(true);

        try {
            await axios.delete(
                `${apiPath}/${mediaId}`,
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            await fetchItems();

            setFlashMessage(
                t('SUCCESS_DELETED_MESSAGE'),
                'success'
            );

        } catch (error) {
            setFlashMessage(
                t('ERROR_MESSAGE'),
                'error'
            );
        }

        setIsLoading(false);
    };

    const handleRankingUpdate = async (
        direction,
        itemId
    ) => {
        if (!itemId) return;

        setIsLoading(true);

        try {
            await axios.put(
                `${apiPath}/${itemId}`,
                { ranking: direction },
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            await fetchItems();

        } catch (error) {
            setFlashMessage(
                t('ERROR_MESSAGE'),
                'error'
            );
        }

        setIsLoading(false);
    };

    return (
        <div style={{ marginTop: '20px' }}>

            {mediaList.map((media, index) => (
                <MediaItem
                    key={media?.id || index}
                    media={media}
                    handleDelete={() =>
                        handleDelete(media?.id)
                    }
                    handleEdit={() => {}}
                    handleRankingUpdate={
                        handleRankingUpdate
                    }
                    isFirst={index === 0}
                    isLast={
                        index ===
                        mediaList.length - 1
                    }
                    isShortLine={
                        isShortLine ||
                        index !==
                        mediaList.length - 1
                    }
                />
            ))}

            {mediaList.length < maxItems &&
                isModeAdmin && (
                <div className="text-center">

                    <Button
                        variant="primary"
                        onClick={handleAddPhoto}
                    >
                        <FontAwesomeIcon
                            icon={faImage}
                        />{' '}
                        Ajouter une photo ici
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleAddVideo}
                    >
                        <FontAwesomeIcon
                            icon={faVideo}
                        />{' '}
                        Ajouter une vidéo YouTube ici
                    </Button>

                </div>
            )}

            <MediaForm
                showModal={showModal}
                handleCloseModal={handleCloseModal}
                handleSubmit={handleSubmit}
                handleFileChange={handleFileChange}
                handleVideoUrlChange={
                    handleVideoUrlChange
                }
                renderInputFields={() => {}}
                availableLanguages={
                    availableLanguages
                }
                selectedAction={selectedAction}
            />

        </div>
    );
}

export default MediaList;